import dayjs, { Dayjs } from 'dayjs';
import moment from 'moment';

const deleteComma = (array: Array<boolean | string>): any => {
  if (!array[array.length - 1]) {
    return deleteComma(array.splice(0, array.length - 1));
  }

  if (!array[0]) {
    return deleteComma(array.splice(1, array.length - 1));
  }

  return array;
};

export const sliceDateTimeToDayjsUtcString = (value: Dayjs | null) => {
  return value ? dayjs(value).format('YYYY-MM-DDTHH:mm:ss.SSSZ').toString().slice(0, 23) + 'Z' : null;
};

export const sliceTimeToUtcWithBaseDate = (value: Dayjs | null) => {
  return value ? dayjs(value).format('1970-01-01THH:mm:ss.SSSZ').toString().slice(0, 23) + 'Z' : null;
};

export const transformDateTimeToDayjsUtc = (value: Dayjs | null) => {
  return value ? dayjs(value).utc() : null;
};

export const formatDays = (days: any) => {
  const ref: any = [];
  const strings: string[] = [];

  let results: string[] = [];
  let startIndex: number = 0;

  days = deleteComma(days);

  for (let i = 0; i < days.length; i++) {
    if (i === 0) {
      strings.push(days[i]);
    } else if (!days[i]) {
      strings.push(',');
    } else if (days[i - 1] && i !== days.length - 1 && days[i + 1]) {
      strings.push('-');
    } else if (days[i]) {
      strings.push(days[i]);
    } else {
      strings.push(',');
    }
  }

  for (let i = 0; i < strings.length; i++) {
    if (strings[i] === ',') {
      ref.push([strings.slice(startIndex, i)]);
      startIndex = i + 1;
    }

    if (i === strings.length - 1) {
      ref.push([strings.slice(startIndex, i + 1)]);
    }
  }

  for (let i = 0; i < ref.length; i++) {
    const array = Array.from(new Set(...ref[i]));

    if (array.includes('-')) {
      results.push(array.join(''));
    } else {
      results.push(array.join(', '));
    }
  }

  return results.join(', ');
};

export const convertToDayJsTime = (dateTime: any) => {
  return moment(dateTime).format('HH:mm') !== 'Invalid date' ? dateTime.format('HH:mm') : null;
};

export const converDateWithResetTime = (dateTime: string, isStartDay: boolean) => {
  const date = moment(dateTime).tz('America/Guatemala');

  return isStartDay
    ? date.set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      })
    : date.set({
        hour: 23,
        minute: 59,
        second: 59,
        millisecond: 999,
      });
};

export const transformDateToCurrentDate = (isoDatetimeStr: any) => {
  const oldDate = new Date(isoDatetimeStr);
  const today = new Date();
  const hours = oldDate.getUTCHours();
  const minutes = oldDate.getUTCMinutes();
  const seconds = oldDate.getUTCSeconds();
  const milliseconds = oldDate.getUTCMilliseconds();
  today.setUTCHours(hours, minutes, seconds, milliseconds);
  return today.toISOString();
};

export enum Daypart {
  Breakfast = 'breakfast',
  Lunch = 'lunch',
  Snack = 'snack', // Kept for backward compatibility if needed, though not in ranges
  Dinner = 'dinner',
  Morning = 'morning', // Kept for backward compatibility
  Afternoon = 'afternoon', // Kept for backward compatibility
  ExtendedBreakfast = 'extended breakfast time',
  Snack1 = 'snack 1',
  Snack2 = 'snack 2',
  ExtendedEvening = 'extended evening hours',
}

type Meal = string;

export const parseHour = (time: string): number => {
  const [h, m] = time.split(':').map(Number);
  return h + m / 60;
};

export const getMealType = (startTime: string, endTime: string): Meal => {
  const start = parseHour(startTime);
  const end = parseHour(endTime);

  const ranges = [
    { name: Daypart.ExtendedBreakfast, start: 4, end: 6 }, // 4, 5
    { name: Daypart.Breakfast, start: 6, end: 11 }, // 6, 7, 8, 9, 10
    { name: Daypart.Lunch, start: 11, end: 15 }, // 11, 12, 13, 14
    { name: Daypart.Snack1, start: 15, end: 17 }, // 15, 16
    { name: Daypart.Dinner, start: 17, end: 21 }, // 17, 18, 19, 20
    { name: Daypart.Snack2, start: 21, end: 24 }, // 21, 22, 23 (End 24 covers up to 24:00)
    { name: Daypart.ExtendedEvening, start: 0, end: 4 }, // 0, 1, 2, 3
  ];

  const meals = ranges.filter(r => start < r.end && end > r.start).map(r => r.name);

  return meals.length > 0 ? (meals.join('-') as Meal) : 'Other';
};

export const getDayPart = (value: number) => {
  if (value === 4 || value === 5) {
    return 'extended breakfast time';
  } else if (value >= 6 && value <= 10) {
    return 'breakfast';
  } else if (value >= 11 && value <= 14) {
    return 'lunch';
  } else if (value === 15 || value === 16) {
    return 'snack 1';
  } else if (value >= 17 && value <= 20) {
    return 'dinner';
  } else if (value >= 21 && value <= 23) {
    return 'snack 2';
  } else {
    return 'extended evening hours';
  }
};
